import React, { useCallback, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ProductTeaserView } from './ProductTeaserView';
import { OurServices } from './OurServices';
import { getTrialState } from '../../redux/selectors/selectors';
import { useGetProductTeaser } from '../../redux/selectors/marketingContent';
import { getProductTeaserByProduct } from '../../redux/thunks/appThunks';
import Iframe from 'react-iframe';
import css from '../../app.scss';
import { FederatedModules } from '../../types';
import Fallback from './fallback';
import FirebaseAnalytics from './analytics';
import { usePardotUrl } from '../../utils/usePardotUrl';
import { getDeviceType } from '../../utils/utils';
import { RootState } from 'bootstrap';
import { TrialExtensionPopUp } from './trialExtensionPopUp';
import { useTranslation } from 'react-i18next';

const Config = require('Config');

const firebasePages = {
  Freight: FirebaseAnalytics.PAGES.FREIGHT_PRODUCT_TEASER,
  Factoring: FirebaseAnalytics.PAGES.FACTORING_PRODUCT_TEASER,
  Fuel: FirebaseAnalytics.PAGES.FUEL_PRODUCT_TEASER,
  Credit: FirebaseAnalytics.PAGES.CREDIT_PRODUCT_TEASER,
  'Load Management': FirebaseAnalytics.PAGES.LOAD_MANAGEMENT_PRODUCT_TEASER,
};

const TMS_TITLE = 'ProTransport';

export const MarketingContainer = () => {
  const location = useLocation<Record<string, unknown>>();
  const navigate = useNavigate();
  const [showGetInfo, setShowGetInfo] = useState<boolean>(false);
  const [product, setProduct] = useState<FederatedModules>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const clientIp = useSelector<RootState, string>((state) => state.app.clientIp);
  const dispatch = useDispatch();
  const { pathname } = location;
  const { isTrial, isRegistered, isExpired, isTrialExtensionRequested } = getTrialState();
  const [showExtForm, setShowExtForm] = useState<boolean>(false);
  const features = useSelector<RootState, Array<string>>((state) => state.app.features);
  const ptTrialEnabled = features.includes('05d3e89dfe967c6ec8fcf9314b155dff0543397f10dbf98279656312ca47b70e');
  const trialProducts = [FederatedModules.CREDIT];
  if (ptTrialEnabled) trialProducts.push(TMS_TITLE as FederatedModules);
  const showExtensionPop = isExpired && isTrial && !isTrialExtensionRequested && trialProducts.includes(product);
  const { t } = useTranslation();
  const deviceId = useSelector<RootState, string>((state) => state.app.deviceId);
  const newDeviceInfoUUIDEnabled = features.includes(
    'f4b1b895b37bc2ce1b552e781369cab4bd0f3d732bad6a88f3059a7341e74dfd',
  );

  const logFirebaseEvent = (module: string, page: string, description: string) => {
    FirebaseAnalytics.sendEvent(FirebaseAnalytics.EVENTS.CLICK, module, page, {
      description: description,
      dateTimestamp: new Date().toISOString(),
      userIP: clientIp,
    });
  };

  const logRMISEvent = () => {
    const description = 'Exit RMIS Application';
    window.addEventListener(
      'popstate',
      () => {
        FirebaseAnalytics.sendEvent(
          FirebaseAnalytics.EVENTS.CLICK,
          FirebaseAnalytics.MODULES.FREIGHT,
          FirebaseAnalytics.PAGES.OUR_SERVICES,
          {
            description: description,
            dateTimestamp: new Date().toISOString(),
            userIP: clientIp,
            deviceType: getDeviceType(),
          },
        );
      },
      { once: true },
    );
  };

  useEffect(() => {
    setShowGetInfo(false);
    if (pathname) {
      setProduct(
        Object.values(FederatedModules)
          .find(
            /*
      Need to replace Loads with ProTransport to correctly retrieve the product teaser and
      display the correct title on the teaser.
    **/
            (module) => pathname.includes(module.toLowerCase()),
          )
          .replace('Loads', TMS_TITLE) as FederatedModules,
      );
    }
  }, [pathname]);

  const productTeaser = useGetProductTeaser(product);
  const pardotUrl = usePardotUrl({ url: productTeaser?.marketingUrl ?? '', product });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await dispatch(getProductTeaserByProduct(product));
        setLoading(false);
      } catch (error) {
        console.log('Failed to get product teaser:', error);
        setLoading(false);
      }
    };

    if (product && !productTeaser && (!newDeviceInfoUUIDEnabled || deviceId)) {
      loadData();
    }

    if (productTeaser) setLoading(false);
  }, [product, deviceId]);

  const handleAskAbout = useCallback(() => {
    logFirebaseEvent(FirebaseAnalytics.MODULES.PRODUCT_TEASERS, firebasePages[product], `ask ${product}`);
    navigate(`${location.pathname}`, {
      state: {
        showGetInfo: true,
      },
    });
  }, [location, product]);

  if (loading && !showGetInfo && !productTeaser) {
    return <Fallback />;
  }

  if (!productTeaser) {
    if (product === FederatedModules.BANKING) {
      window.location.href = `${Config.rtsproHomepage}`;
      return null;
    }
    return <div>{t('product_teaser_not_found')}</div>;
  }

  if (location.state && location.state.showGetInfo) {
    if (product === FederatedModules.FREIGHT) {
      return (
        <Iframe
          url={pardotUrl}
          width="100%"
          height="100%"
          className={css.frame}
          position="relative"
          onLoad={logRMISEvent}
        />
      );
    }
    return isTrial && !isRegistered ? (
      <OurServices
        serviceKey={product}
        headerText={productTeaser.additionalProps.ourServices.headerText}
        bodyText={productTeaser.additionalProps.ourServices.bodyText}
        logFirebaseEvent={logFirebaseEvent}
      />
    ) : (
      <Iframe url={pardotUrl} width="100%" height="100%" className={css.frame} position="relative" />
    );
  }

  return (
    <>
      {showExtensionPop && (
        <div key={`${product}-trial-popup`} className={css.frame}>
          <TrialExtensionPopUp setShowExtForm={setShowExtForm} />
        </div>
      )}
      {showExtForm ? (
        <OurServices
          serviceKey={t('extend_trial')}
          headerText={t('credit_extention_request')}
          bodyText={t('thank_you_for_your_interest_in_credit_complete_this_form_and_we_will_review_your_request')}
          logFirebaseEvent={() => {}}
        />
      ) : (
        <ProductTeaserView
          handleAskAbout={handleAskAbout}
          product={product}
          productTeaser={productTeaser}
          logFirebaseEvent={logFirebaseEvent}
          firebasePages={firebasePages}
        />
      )}
    </>
  );
};
